// Add a completed nap or backfill an entry on the viewed day.
// Pick a type, a time on the viewed day, and the type-specific detail.
// Inserts with an explicit `at`, so it lands on the day being viewed.

import { useState } from "react";
import type { EventsApi } from "../lib/useEvents";
import { useT, useAmount } from "../lib/LocaleContext";

type Kind = "feed" | "nurse" | "sleep" | "diaper" | "note";

type Props = {
  open: boolean;
  napOnly?: boolean;
  /** Initial date for the entry. */
  viewDate: Date;
  /** Resolved quick-log amounts (baby default → last logged value). */
  quickFeedOz: number;
  quickNurseOz: number;
  insert: EventsApi["insert"];
  onClose: () => void;
};

export function AddEntrySheet({ open, napOnly = false, viewDate, quickFeedOz, quickNurseOz, insert, onClose }: Props) {
  const t = useT();
  const [kind, setKind] = useState<Kind>(napOnly ? "sleep" : "feed");
  if (!open) return null;

  const kinds: [Kind, string][] = [
    ["feed", t.addEntry.typeBottle],
    ["nurse", t.addEntry.typeNursing],
    ["sleep", t.addEntry.typeSleep],
    ["diaper", t.addEntry.typeDiaper],
    ["note", t.addEntry.typeNote],
  ];

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={styles.handle} />
        <h2 style={styles.title}>{napOnly ? t.logSheet.addNap : t.addEntry.title}</h2>

        {!napOnly && <div style={styles.field}>
          <div style={styles.fieldLabel}>{t.addEntry.fieldType}</div>
          <div style={styles.seg}>
            {kinds.map(([val, label]) => (
              <button
                key={val}
                style={{
                  ...styles.segItem,
                  background: val === kind ? "var(--bh-ink)" : "var(--bh-surface-2)",
                  color: val === kind ? "var(--bh-surface)" : "var(--bh-ink-2)",
                  fontWeight: val === kind ? 600 : 500,
                }}
                onClick={() => setKind(val)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>}

        {/* Keyed by kind so switching type re-seeds the fields. */}
        <AddForm
          key={kind}
          napOnly={napOnly}
          kind={kind}
          viewDate={viewDate}
          defaultOz={kind === "nurse" ? quickNurseOz : quickFeedOz}
          insert={insert}
          onClose={onClose}
        />
      </div>
    </div>
  );
}

function AddForm({
  napOnly,
  kind,
  viewDate,
  defaultOz,
  insert,
  onClose,
}: {
  napOnly: boolean;
  kind: Kind;
  viewDate: Date;
  defaultOz: number;
  insert: EventsApi["insert"];
  onClose: () => void;
}) {
  const t = useT();
  const amount = useAmount();
  const [date, setDate] = useState(() => `${viewDate.getFullYear()}-${String(viewDate.getMonth() + 1).padStart(2, "0")}-${String(viewDate.getDate()).padStart(2, "0")}`);
  const [atTime, setAtTime] = useState("12:00");
  const [endTime, setEndTime] = useState("");
  const [oz, setOz] = useState(defaultOz);
  const [amtText, setAmtText] = useState(() => String(amount.value(defaultOz)));
  const [diaperKind, setDiaperKind] = useState<"wet" | "dirty" | "both">("wet");
  const [noteText, setNoteText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  // A backdated open-ended sleep would read as "asleep right now" on the Today
  // screen, so on a past day the end time is required.
  const valid = date !== "" && atTime !== "" && (
    kind === "sleep" ? endTime !== "" && endTime !== atTime :
    kind === "note" ? noteText.trim() !== "" :
    kind === "diaper" ? true :
    oz > 0);

  async function handleAdd() {
    if (!valid || busy) return;
    setBusy(true);
    setError(false);
    const caretaker = localStorage.getItem("babyhelper:caretakerLabel") ?? undefined;
    const day = new Date(`${date}T12:00:00`);
    const at = onDayIso(day, atTime);
    try {
      if (kind === "feed") await insert({ type: "feed", at, amount_oz: oz, caretaker_label: caretaker });
      if (kind === "nurse") await insert({ type: "nurse", at, amount_oz: oz, caretaker_label: caretaker });
      if (kind === "sleep") {
        let end = onDayIso(day, endTime);
        // An end before the start means the sleep crossed midnight.
        if (end <= at) end = onDayIso(addDays(day, 1), endTime);
        const startHour = Number(atTime.split(":")[0]);
        await insert({
          type: "sleep",
          at,
          end_at: end,
          sleep_kind: napOnly ? "nap" : startHour >= 19 || startHour < 7 ? "night" : "nap",
          caretaker_label: caretaker,
        });
      }
      if (kind === "diaper") await insert({ type: "diaper", at, diaper_kind: diaperKind, caretaker_label: caretaker });
      if (kind === "note") await insert({ type: "note", at, note: noteText.trim(), caretaker_label: caretaker });
      onClose();
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {napOnly && <Field label={t.addEntry.fieldDate}>
        <input type="date" style={styles.input} value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>}
      <Field label={kind === "sleep" ? t.editEvent.fieldStarted : t.editEvent.fieldTime}>
        <input type="time" style={styles.input} value={atTime} onChange={(e) => setAtTime(e.target.value)} />
      </Field>

      {kind === "sleep" && (
        <Field label={t.editEvent.fieldEnded} hint={endTime || napOnly ? undefined : t.addEntry.endRequiredHint}>
          <input type="time" style={styles.input} value={endTime} onChange={(e) => setEndTime(e.target.value)} />
        </Field>
      )}

      {(kind === "feed" || kind === "nurse") && (
        <Field label={t.editEvent.fieldAmount(amount.word)}>
          <input
            type="number"
            inputMode="decimal"
            step={amount.unit === "ml" ? "5" : "0.5"}
            min="0"
            style={styles.input}
            value={amtText}
            onChange={(e) => {
              const raw = e.target.value;
              setAmtText(raw);
              const n = Number(raw);
              setOz(raw.trim() !== "" && Number.isFinite(n) ? amount.toOz(n) : 0);
            }}
          />
        </Field>
      )}

      {kind === "diaper" && (
        <Field label={t.editEvent.fieldKind}>
          <div style={styles.seg}>
            {([["wet", t.editEvent.kindWet], ["dirty", t.editEvent.kindDirty], ["both", t.editEvent.kindBoth]] as const).map(
              ([val, label]) => (
                <button
                  key={val}
                  style={{
                    ...styles.segItem,
                    background: val === diaperKind ? "var(--bh-ink)" : "var(--bh-surface-2)",
                    color: val === diaperKind ? "var(--bh-surface)" : "var(--bh-ink-2)",
                    fontWeight: val === diaperKind ? 600 : 500,
                  }}
                  onClick={() => setDiaperKind(val)}
                >
                  {label}
                </button>
              ),
            )}
          </div>
        </Field>
      )}

      {kind === "note" && (
        <Field label={t.addEntry.typeNote}>
          <input
            style={styles.input}
            value={noteText}
            placeholder={t.addEntry.notePlaceholder}
            onChange={(e) => setNoteText(e.target.value)}
          />
        </Field>
      )}

      {error && <p role="alert">{t.addEntry.saveError}</p>}
      <div style={styles.actions}>
        <button style={styles.ghost} onClick={onClose}>
          {t.editEvent.cancel}
        </button>
        <button style={{ ...styles.primary, opacity: valid && !busy ? 1 : 0.4 }} disabled={!valid || busy} onClick={handleAdd}>
          {napOnly ? t.logSheet.addNap : t.addEntry.save}
        </button>
      </div>
    </>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label style={{ ...styles.field, display: "block" }}>
      <div style={styles.fieldLabel}>{label}</div>
      {children}
      {hint && <div style={styles.hint}>{hint}</div>}
    </label>
  );
}

function onDayIso(day: Date, hhmm: string): string {
  const d = new Date(day);
  const [h, m] = hhmm.split(":").map(Number);
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}

function addDays(day: Date, n: number): Date {
  const d = new Date(day);
  d.setDate(d.getDate() + n);
  return d;
}

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "oklch(0 0 0 / 0.55)",
    zIndex: 110,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
  },
  sheet: {
    background: "var(--bh-surface)",
    width: "100%",
    maxWidth: 520,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    padding: "10px 16px max(20px, env(safe-area-inset-bottom))",
    boxShadow: "0 -20px 60px oklch(0 0 0 / 0.4)",
    maxHeight: "92vh",
    overflowY: "auto",
  },
  handle: { width: 40, height: 4, background: "var(--bh-divider)", borderRadius: 2, margin: "0 auto 14px" },
  title: { fontSize: 20, fontWeight: 600, margin: "0 0 16px", color: "var(--bh-ink)" },
  field: { marginBottom: 14 },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "var(--bh-ink-3)",
    marginBottom: 6,
  },
  hint: { fontSize: 12, color: "var(--bh-ink-3)", marginTop: 5 },
  input: {
    width: "100%",
    appearance: "none",
    background: "var(--bh-surface-2)",
    border: "1px solid var(--bh-divider)",
    borderRadius: 12,
    color: "var(--bh-ink)",
    fontSize: 17,
    fontFamily: "inherit",
    padding: "12px 12px",
    outline: "none",
    boxSizing: "border-box",
  },
  seg: { display: "flex", gap: 6, flexWrap: "wrap" },
  segItem: {
    flex: 1,
    appearance: "none",
    border: "none",
    borderRadius: 12,
    padding: "12px 4px",
    fontSize: 15,
    cursor: "pointer",
    minHeight: 48,
    minWidth: 72,
  },
  actions: { display: "flex", gap: 8, marginTop: 18 },
  ghost: {
    flex: 1,
    background: "var(--bh-surface-2)",
    color: "var(--bh-ink-2)",
    border: "none",
    padding: "14px",
    fontSize: 16,
    fontWeight: 500,
    borderRadius: 14,
    cursor: "pointer",
    minHeight: 56,
  },
  primary: {
    flex: 2,
    background: "var(--bh-ink)",
    color: "var(--bh-surface)",
    border: "none",
    padding: "14px",
    fontSize: 16,
    fontWeight: 600,
    borderRadius: 14,
    cursor: "pointer",
    minHeight: 56,
  },
};
